import { Modal } from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { handleDownload } from "../utils/handleDownload";
import ImageViewer from "react-simple-image-viewer";
import { CameraAlt, Delete } from "@mui/icons-material";
import { showError } from "../constants/toasts";
import { errorToast } from "../helpers";
import { UploadMedia } from "../utils/uploadMedia";
import Loader from "../constants/Loader";

interface PassportModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  idData?: {
    idCard: string;
    residenceCard: string;
  };
  setIdData?: any;
}

export default function PassportModal({
  open,
  onClose,
  setOpen,
  idData,
  setIdData,
}: PassportModalProps) {
  const [currentImage, setCurrentImage] = useState("");

  const idCardInputRef = useRef<HTMLInputElement>(null);
  const residenceCardInputRef = useRef<HTMLInputElement>(null);

  const closeImageViewer = () => {
    setCurrentImage("");
  };

  enum imageType {
    idCard = 1,
    residenceCard = 2,
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: imageType
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) return;

      const allowedExtensions = ["png", "jpg", "jpeg"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (!allowedExtensions.includes(fileExtension || "")) {
        showError("Invalid file format: only png, jpg images are allowed");
        return;
      }

      const res = await UploadMedia(file, fileExtension);

      if (res?.statusCode === 200) {
        if (type === imageType.idCard) {
          setIdData((prev) => ({ ...prev, idCard: res?.data }));
        } else {
          setIdData((prev) => ({ ...prev, residenceCard: res?.data }));
        }
      } else {
        errorToast(res?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
    }
  };

  return (
    <Modal
      className="modal passport_modal"
      id="passportModal"
      aria-labelledby="passport-modal-title"
      aria-describedby="passport-modal-description"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="btn-close" onClick={() => setOpen(false)}>
            <CloseIcon />
          </div>
          <div className="modal_title">
            <h2>Passport / ID</h2>
            <div className="btn_flex">
              {/* Optional buttons for QR or Fingerprint */}
            </div>
          </div>

          {/* ID Card Section */}
          {(idData?.idCard || setIdData) && (
            <div className="main_title" style={{ textAlign: "center" }}>
              <p>Id Card</p>
              {setIdData && idData?.idCard && (
                <div
                  onClick={() => setIdData((prev) => ({ ...prev, idCard: "" }))}
                >
                  <Delete
                    style={{
                      zIndex: 1,
                      cursor: "pointer",
                      alignSelf: "center",
                      backgroundColor: "red",
                      color: "#fff",
                      pointerEvents: "unset",
                      position: "absolute",
                      borderRadius: "50%",
                      padding: 5,
                      width: 25,
                      height: 25,
                      marginLeft: "-2%",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {(idData?.idCard || setIdData) && (
            <figure
              className="passport_img"
              style={{ marginTop: "-20px", position: "relative" }}
              onClick={() => idData?.idCard && setCurrentImage(idData?.idCard)}
            >
              <img
                src={idData?.idCard || "/static/images/passport_attachment.jpg"}
                alt="ID Card"
              />
              {setIdData && !idData?.idCard ? (
                <CameraAlt
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    bottom: 0,
                    background: "lightgrey",
                    borderRadius: "50%",
                    padding: 5,
                    width: 35,
                    height: 35,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    idCardInputRef.current?.click();
                  }}
                />
              ) : null}
              <input
                type="file"
                ref={idCardInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => handleImageUpload(e, imageType.idCard)}
              />
            </figure>
          )}

          {/* Residence Card Section */}
          {(idData?.residenceCard || setIdData) && (
            <div
              className="main_title"
              style={{ textAlign: "center", marginTop: "20px" }}
            >
              <p>Residence Card</p>
              {setIdData && idData?.residenceCard && (
                <div
                  onClick={() =>
                    setIdData((prev) => ({
                      ...prev,
                      residenceCard: "",
                    }))
                  }
                >
                  <Delete
                    style={{
                      zIndex: 1,
                      cursor: "pointer",
                      alignSelf: "center",
                      backgroundColor: "red",
                      color: "#fff",
                      pointerEvents: "unset",
                      position: "absolute",
                      borderRadius: "50%",
                      padding: 5,
                      width: 25,
                      height: 25,
                      marginLeft: "-2%",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {(idData?.residenceCard || setIdData) && (
            <figure
              className="passport_img"
              style={{ marginTop: "-20px", position: "relative" }}
              onClick={() =>
                idData?.residenceCard && setCurrentImage(idData?.residenceCard)
              }
            >
              <img
                src={
                  idData?.residenceCard ||
                  "/static/images/passport_attachment.jpg"
                }
                alt="Residence Card"
              />
              {setIdData && !idData?.residenceCard ? (
                <CameraAlt
                  style={{
                    position: "absolute",
                    cursor: "pointer",
                    bottom: 0,
                    background: "lightgrey",
                    borderRadius: "50%",
                    padding: 5,
                    width: 35,
                    height: 35,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    residenceCardInputRef.current?.click();
                  }}
                />
              ) : null}
              <input
                type="file"
                ref={residenceCardInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => handleImageUpload(e, imageType.residenceCard)}
              />
            </figure>
          )}

          {/* Footer */}
          <div className="footer_btn_flex">
            <Button
              className="btn btn_secondary"
              onClick={() => setOpen(false)}
            >
              Okay
            </Button>
            <Button
              className="btn btn_highlight"
              onClick={() => {
                const temp: any = idData;
                for (let key in temp) {
                  if (temp[key]) {
                    handleDownload(temp[key], "id");
                  }
                }
              }}
            >
              Download
            </Button>
          </div>
        </div>

        {/* Image Viewer */}
        {currentImage && (
          <ImageViewer
            src={[currentImage]}
            onClose={closeImageViewer}
            disableScroll={false}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
              padding: "5%",
            }}
            closeOnClickOutside={true}
          />
        )}
      </div>
    </Modal>
  );
}
